import { isObjectType, isString } from 'remeda';

import ApiErrorCodes from '@@api/constants/errorCodes';
import { type Metadata, type Tag, type Teaser } from '@@api/services/metadata/schemas';
import { type SectionContentItem } from '@@api/services/placement/schemas';
import { type Category, type Tenant } from '@@api/services/tenant/schemas';
import { getDefaultTenantDefaultTeaser } from '@@containers/Teaser/utils';
import { convertToNumber } from '@@scripts/utils/idUtils';

import { COLUMN_TYPE, type ColumnType } from './components/Column/constants';
import {
    NON_EXPANDABLE_SECTION_TYPES,
    SectionContentItemTypes,
    type SectionType,
} from './constants';
import {
    type ExpandableSection,
    type PlacementColumn,
    type PlacementDragSourceItem,
} from './types';

export const isExpandableSection = (section: { type: SectionType }): section is ExpandableSection =>
    !NON_EXPANDABLE_SECTION_TYPES.has(section.type);

const SEPARATOR = '_';

export const PLACEHOLDER_ID = `0${SEPARATOR}0`;

export const serializeCategoryColumnId = (tenantId: Tenant['id'], categoryId: Category['id']) =>
    `${COLUMN_TYPE.CATEGORY}${tenantId}${SEPARATOR}${categoryId}`;

export const serializeTagColumnId = (tenantId: Tenant['id'], tagId: Tag['id']) =>
    `${COLUMN_TYPE.TAG}${tenantId}${SEPARATOR}${tagId}`;

export const deserializeColumnId = (columnId: PlacementColumn): [ColumnType, PlacementColumn] => {
    const columnType = columnId[0] as ColumnType;
    const id = columnId.slice(1);

    return [columnType, id];
};

export const deserializeColumnTypeId = (
    columnId: PlacementColumn,
): [Tenant['id'], Category['id'] | Tag['id']] => {
    const id = deserializeColumnId(columnId)[1];

    const [tenantId, pageTypeId] = id.split(SEPARATOR).map(convertToNumber);

    return [tenantId, pageTypeId];
};

export const getOpenColumnCategoriesByTenantId = (
    tenantId: Tenant['id'],
    columns: PlacementColumn[],
): Category['id'][] =>
    columns
        .filter((columnId) => deserializeColumnId(columnId)[0] === COLUMN_TYPE.CATEGORY)
        .map((columnId) => deserializeColumnTypeId(columnId))
        .filter(([tenantId_]) => tenantId_ === tenantId)
        .map(([, categoryId]) => categoryId);

export const getOpenColumnTagsByTenantId = (
    tenantId: Tenant['id'],
    columns: PlacementColumn[],
): Tag['id'][] =>
    columns
        .filter((columnId) => deserializeColumnId(columnId)[0] === COLUMN_TYPE.TAG)
        .map((columnId) => deserializeColumnTypeId(columnId))
        .filter(([tenantId_]) => tenantId_ === tenantId)
        .map(([, tagId]) => tagId);

export const getSectionContentItemId = (item: SectionContentItem) => {
    if (item.sectionContentItemType === SectionContentItemTypes.PLACEHOLDER) {
        return `${item.placeholderTenantId}_${item.placeholderCategoryId}_${item.placeholderIndexPosition}`;
    }

    return item.metadataId;
};

// @ts-expect-error TS7006 Issue #4120
export const isSameNotNull = (property) => (source, target) =>
    source[property] !== null && source[property] === target[property];

// @ts-expect-error TS7006 Issue #4120
export const isSameProperties = (properties) => (source, target) =>
    // @ts-expect-error TS7006 Issue #4120
    properties.map(isSameNotNull).every((fn) => fn(source, target));
export const isSameColumn = isSameProperties(['columnId']);
const isSameSectioncontentItemType = isSameNotNull('sectionContentItemType');
const isSameMetadataId = isSameNotNull('metadataId');
const isSamePlaceholderCategory = isSameProperties([
    'placeholderType',
    'placeholderTenantId',
    'placeholderCategoryId',
    'placeholderIndexPosition',
]);

const placeholderComparators = {
    category: isSamePlaceholderCategory,
};

// @ts-expect-error TS7006 Issue #4120
const isSamePlaceholder = (a, b) => placeholderComparators[a.placeholderType](a, b);

const sectionContentItemComparators = {
    [SectionContentItemTypes.DIRECT_PLACEMENT]: isSameMetadataId,
    [SectionContentItemTypes.PLACEHOLDER]: isSamePlaceholder,
};

// @ts-expect-error TS7006 Issue #4120
export const isEqualSectionContentItem = (a, b) => {
    // @ts-expect-error TS7053 Issue #4120
    const compareSectionContentItem = sectionContentItemComparators[a.sectionContentItemType];

    return isSameSectioncontentItemType(a, b) && compareSectionContentItem(a, b);
};

export const isPlacementTenantError = (body: unknown) =>
    isObjectType(body) &&
    'status' in body &&
    'message' in body &&
    body.status === ApiErrorCodes.BAD_REQUEST &&
    isString(body.message) &&
    body.message.includes('are not assigned to tenant');

export const COPY_SECTION_ERROR_MESSAGES = {
    'At least one source section category is missing in the target sitemap.':
        'SECTION_CATEGORY_IS_MISSING_IN_THE_TARGET_SITEMAP',
    "Section can't be copied to its referenced Category Page.":
        'SECTION_CANNOT_BE_COPIED_TO_ITS_REFERENCED_PAGE',
    'Default Sections cannot be copied.': 'DEFAULT_SECTION_CANNOT_BE_COPIED',
};

export const isPlacementCopySectionError = (body: unknown) => {
    const errorMessages = Object.keys(COPY_SECTION_ERROR_MESSAGES);

    return (
        isObjectType(body) &&
        'status' in body &&
        'message' in body &&
        body.status === ApiErrorCodes.BAD_REQUEST &&
        isString(body.message) &&
        errorMessages.includes(body.message)
    );
};

// @ts-expect-error TS7006 Issue #4120
export const getBookmarkedMetadataList = (metadataList, bookmarkList) =>
    // @ts-expect-error TS7006 Issue #4120
    metadataList.map((metadata) => {
        if (!metadata) {
            return null;
        }

        // @ts-expect-error TS7006 Issue #4120
        const bookmark = bookmarkList.find((bookmark) => bookmark?.metadataId === metadata?.id);

        return {
            ...metadata,
            bookmarkId: bookmark?.id || null,
        };
    });

// @ts-expect-error TS7006 Issue #4120
export const getMetadataListForBookmarks = (bookmarkList, metadataList) =>
    // @ts-expect-error TS7006 Issue #4120
    bookmarkList.map((bookmark) => {
        // @ts-expect-error TS7006 Issue #4120
        const metadata = metadataList.find((metadata) => metadata?.id === bookmark?.metadataId);

        if (!metadata) {
            return metadata;
        }

        return {
            ...metadata,
            bookmarkId: bookmark?.id || null,
        };
    });

export const getDefaultVariantTeaser = <T extends Metadata>(entity: T) =>
    getDefaultTenantDefaultTeaser(entity);

export const getPlacementDragSourceItem = (
    entity: (Metadata & Teaser) | null | undefined,
    dragSourceItemProps?: PlacementDragSourceItem,
) =>
    ({
        ...dragSourceItemProps,
        metadataId: entity?.id,
        tenantIds: entity?.tenantIds || [],
    }) as PlacementDragSourceItem;
